import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  BanIcon,
  InfoIcon,
  LightbulbIcon,
  SendIcon,
  UserPlusIcon,
} from "lucide-react";

import type { APIError } from "simplydo/core/api";
import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type {
  IdeaDetail,
  IdeaTeamInvitation,
  UpdateIdeaFn,
} from "components/ideas/types";
import { UserChip } from "components/lib/Chips";
import type { ChipUser } from "components/lib/Chips/types";
import UserChooser from "components/lib/Choosers/UserChooser/UserChooser";
import { IdeaTeamStartChat } from "components/lib/Ideas";
import RequestJoinModal from "components/lib/Ideas/RequestJoinModal";
import { TooltipButton } from "components/lib/UI";

type RequestIdeaTeamProps = { idea: IdeaDetail; updateIdea: UpdateIdeaFn };

export const RequestIdeaTeam = ({ idea, updateIdea }: RequestIdeaTeamProps) => {
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  return (
    <>
      <RequestJoinModal
        idea={idea}
        open={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
        onConfirm={() => {
          updateIdea({ requestedToJoin: true }, ["requestedToJoin"], true);
        }}
      />
      <Button
        size="small"
        disabled={idea.requestedToJoin}
        fluid
        style={{ marginTop: 10 }}
        onClick={() => setJoinModalOpen(true)}
      >
        <Icon icon={SendIcon} />
        {idea.requestedToJoin
          ? "Awaiting approval to join"
          : "Request to join team"}
      </Button>
    </>
  );
};

type IdeaTeamProps = {
  idea: IdeaDetail;
  leaveIdea: () => void;
  canEdit: boolean;
  updateIdea: UpdateIdeaFn;
  isSidebar?: boolean;
};

const IdeaTeam = ({ idea, leaveIdea, canEdit, updateIdea }: IdeaTeamProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState<IdeaTeamInvitation[]>([]);
  const [_isUpdating, setIsUpdating] = useState(false);

  const ideaId = idea?._id;
  const hasBusinessProfile = !!idea?.ideaBusinessProfile;

  const getInvitations = useCallback(() => {
    if (!ideaId) {
      return;
    }
    api.invitations.getForType(
      "idea",
      ideaId,
      ({ invitations: newInvitations }) => {
        setInvitations(newInvitations);
      },
      () => {},
    );
  }, [ideaId]);

  useEffect(() => getInvitations(), [getInvitations]);

  const makeOwner = useCallback(
    (teammate: OpenAPI.Schemas["PreparedUser"]) => {
      setIsUpdating(true);
      util
        .confirm(
          t("ideas.teammates.owner.title"),
          t("ideas.teammates.owner.info", { teammate }),
        )
        .then(() => {
          api.ideas.updateOwner(
            ideaId,
            teammate._id,
            ({ collaborators }) => {
              updateIdea(teammate._id, ["user"], true);
              updateIdea(collaborators, ["collaborators"], true);
              setIsUpdating(false);
            },
            (err) => {
              toast.error(err.message);
              setIsUpdating(false);
            },
          );
        })
        .catch(() => {
          setIsUpdating(false);
        });
    },
    [ideaId, updateIdea, t],
  );

  const searchForCollaborators = useCallback(
    (
      term: string,
      cb: (users: OpenAPI.Schemas["PreparedUser"][]) => void,
      fail: (error: APIError) => void,
    ) => {
      api.search.ideaCollaborators(
        ideaId,
        term,
        ({ users }) => {
          cb(users);
        },
        fail,
      );
    },
    [ideaId],
  );

  const addTeammateMulti = useCallback(
    (teammateIds: string[]) => {
      api.ideas.addCollaboratorMulti(
        ideaId,
        teammateIds,
        ({ authors = [], collaborators = [] }) => {
          updateIdea(authors, ["authors"], true);
          updateIdea(collaborators, ["collaborators"], true);
        },
        (err) => toast.error(err.message),
      );
    },
    [ideaId, updateIdea],
  );

  const removeTeammate = useCallback(
    (teammate: OpenAPI.Schemas["PreparedUser"]) => {
      util
        .confirm(
          t("ideas.teammates.remove.title"),
          t("ideas.teammates.remove.info", { teammate }),
        )
        .then(() => {
          api.ideas.removeCollaborator(
            ideaId,
            teammate._id,
            ({ authors, collaborators }) => {
              updateIdea(authors, ["authors"], true);
              updateIdea(collaborators, ["collaborators"], true);
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {});
    },
    [ideaId, t, updateIdea],
  );

  const removeInvite = useCallback(
    (invitation: IdeaTeamInvitation) => {
      util
        .confirm(
          t("ideas.invitations.remove.title"),
          t("ideas.invitations.remove.info"),
        )
        .then(() => {
          api.invitations.remove(
            invitation._id,
            () => {
              setInvitations((prevInvitations) =>
                prevInvitations.filter((i) => i._id !== invitation._id),
              );
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {});
    },
    [t],
  );

  const teammateActionButton = useCallback(
    (teammate: OpenAPI.Schemas["PreparedUser"]) => {
      if (!user) {
        return null;
      }
      const { collaborators = [] } = idea;
      const teammateIsCollaborator = collaborators.indexOf(teammate._id) > -1;
      const teammateIsOwner = idea.user === teammate._id;
      const currentUserIsOwner = idea.user === user._id;
      const teammateIsCurrentUser = user._id === teammate._id;
      if (!canEdit && !teammateIsCurrentUser) return null;
      if (canEdit && teammateIsCurrentUser && teammateIsOwner) return null;
      if (canEdit && !teammateIsCurrentUser && !currentUserIsOwner) return null;
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
          }}
        >
          <Dropdown direction="left">
            <DropdownMenu>
              {canEdit && !teammateIsOwner && currentUserIsOwner ? (
                <>
                  <DropdownItem onClick={() => makeOwner(teammate)}>
                    <Icon icon={LightbulbIcon} />
                    {t("ideas.teammates.add.owner")}
                  </DropdownItem>
                  <DropdownItem
                    style={{ color: "rgba(200, 10, 30, 0.87)" }}
                    onClick={() => removeTeammate(teammate)}
                  >
                    <Icon icon={BanIcon} />
                    {t("ideas.teammates.remove.removeNow")}
                  </DropdownItem>
                </>
              ) : null}
              {teammateIsCurrentUser &&
              !teammateIsOwner &&
              teammateIsCollaborator ? (
                <DropdownItem
                  style={{ color: "rgba(200, 10, 30, 0.87)" }}
                  onClick={() => leaveIdea()}
                >
                  <Icon icon={BanIcon} />
                  {t("ideas.options.leave")}
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </Dropdown>
        </div>
      );
    },
    [idea, user, t, leaveIdea, canEdit, makeOwner, removeTeammate],
  );

  const invitationActionButton = useCallback(
    (invitation: IdeaTeamInvitation) => {
      if (!canEdit) return null;
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
          }}
        >
          <Dropdown direction="left">
            <DropdownMenu>
              {canEdit ? (
                <DropdownItem
                  style={{ color: "rgba(200, 10, 30, 0.87)" }}
                  onClick={() => removeInvite(invitation)}
                >
                  <Icon icon={BanIcon} />
                  {t("ideas.invitations.remove.removeNow")}
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </Dropdown>
        </div>
      );
    },
    [t, canEdit, removeInvite],
  );

  const invited = useMemo(
    () =>
      (invitations || []).map((i) => ({
        ...i,
        profile: {
          fullName: i.inviteeUser?.profile?.fullName || i.invitee,
          jobTitle: `Invited ${formatDate(i.createdAt, "dd/MM/yy, HH:mm")}`,
        },
      })),
    [invitations],
  );
  if (!user) {
    return null;
  }
  return (
    <div data-testid="idea-team">
      {idea?.user || idea?.collaborators?.length ? (
        <Popup
          on="hover"
          trigger={
            <H5 style={{ color: "#333", marginBottom: 10 }}>
              {t("ideas.teammates.title")}
            </H5>
          }
        >
          {t("ideas.teammates.para")}
        </Popup>
      ) : null}

      {idea?.authors?.map((u) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 8,
            alignItems: "center",
            maxWidth: "100%",
          }}
          key={u._id}
        >
          <UserChip user={u} compact />
          {idea.user === u._id && (
            <Popup
              trigger={
                <Icon
                  size="small"
                  icon={LightbulbIcon}
                  color="violet"
                  style={{ minWidth: 21 }}
                />
              }
              on="hover"
            >{`${t("common:capitalise", { key: "generic.idea" })} owner`}</Popup>
          )}
          {teammateActionButton(u)}
        </div>
      ))}
      {invited.length > 0 && user._id === idea.user ? (
        <>
          <H5 style={{ color: "#333", marginBottom: 10 }}>
            Pending invitations
          </H5>
          {invited.map((u) => (
            <div
              key={u._id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <UserChip
                user={
                  (u.inviteeUser ?? {
                    _id: u._id,
                    profile: u.profile,
                  }) as ChipUser
                }
                compact
              />
              {invitationActionButton(u)}
            </div>
          ))}
        </>
      ) : null}

      {util.canJoinIdeaChat(user, idea) &&
      user?.ownerOrganisation?.enabledFeatures?.includes("directMessaging") ? (
        <IdeaTeamStartChat
          idea={idea}
          style={{ marginTop: 10 }}
          fluid
          size="small"
          basic
        />
      ) : null}
      <UserChooser
        trigger={
          <TooltipButton
            size="small"
            fluid
            style={{ marginTop: util.canJoinIdeaChat(user, idea) ? 5 : 10 }}
            disabled={!canEdit || user._id !== idea.user}
            tooltip={
              (!canEdit || user._id !== idea.user) &&
              `Only the ${t("generic.idea")} owner can add collaborators.`
            }
          >
            <Icon icon={UserPlusIcon} />
            {t("ideas.teammates.add.addNow")}
          </TooltipButton>
        }
        beforeContent={
          hasBusinessProfile ? (
            <Message color="blue" style={{ margin: 0 }}>
              <Icon icon={InfoIcon} />
              <MessageHeader>Business profile access</MessageHeader>
              <MessageContent>
                Your {t("generic.idea")} is connected to a business profile, any
                user that is added as a collaborator will also automatically be
                added as a member to the business profile.
              </MessageContent>
            </Message>
          ) : null
        }
        enabledFeatures={{
          search: !idea?.ownerChallenge?.preventCollaboration,
          invite: true,
          persistentToken: true,
        }}
        searchFunction={searchForCollaborators}
        confirm={t("ideas.team.members.add")}
        clearOnComplete
        forType="idea"
        forId={ideaId}
        onComplete={(users) => {
          const emailUsers = users.filter(
            (
              u,
            ): u is OpenAPI.Schemas["PreparedUser"] & {
              isEmailInvitee: true;
            } => !!u.isEmailInvitee,
          );
          if (emailUsers.length) {
            api.invitations.createBulk(
              {
                invitees: emailUsers.map((u) => u._id),
                invitationType: "email",
                forType: "idea",
                forId: ideaId,
              },
              ({ invitations: newInvitations }) => {
                setInvitations((prevInvitations) => [
                  ...prevInvitations,
                  ...newInvitations,
                ]);
                toast.success(
                  `Invitation${emailUsers.length > 1 ? "s" : ""} sent`,
                );
              },
              () => {},
            );
          }
          const nonEmailUsers = users.filter((u) => !u.isEmailInvitee);
          if (nonEmailUsers.length)
            addTeammateMulti(nonEmailUsers.map((u) => u._id));
          if (users.find((u) => u.isEmailInvitee === undefined))
            toast.success("Collaborators updated");
        }}
      />
    </div>
  );
};

export default IdeaTeam;
